/**
 * LookAhead5DDrawer — 5-dimensional drilldown for a macro-schedule task.
 * Dimensions: Engineering · Procurement · Fabrication & Construction ·
 * Manpower · Equipment.  Plus a Cognara analysis card with cautions,
 * monitoring focus, action plan, catch-up plan and cross-impact.
 */
import { useEffect, useState } from 'react';
import React from 'react';
import { X, Sparkles, Loader2, Factory, Package, Hammer, Users, Wrench,
         AlertTriangle, Eye, ListChecks, TrendingDown, GitBranch, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import CognaraRaidPushInline from '@/components/scheduling/CognaraRaidPushInline';

const RISK_STYLE = {
  HIGH:   { bg: 'bg-red-100 dark:bg-red-500/20',     tx: 'text-red-700 dark:text-red-300' },
  MEDIUM: { bg: 'bg-amber-100 dark:bg-amber-500/20', tx: 'text-amber-700 dark:text-amber-300' },
  LOW:    { bg: 'bg-emerald-100 dark:bg-emerald-500/20', tx: 'text-emerald-700 dark:text-emerald-300' },
};

const STATUS_COLOR = (s) => {
  const up = (s || '').toUpperCase();
  if (up === 'DELAYED' || up === 'AT_RISK') return 'text-red-600 dark:text-red-400';
  if (up === 'COMPLETED')                    return 'text-emerald-600 dark:text-emerald-400';
  if (up === 'IN_PROGRESS' || up === 'ON_TRACK') return 'text-sky-600 dark:text-sky-400';
  return 'text-slate-500 dark:text-slate-400';
};

function Section({ icon: Icon, title, count, children, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-[#0a0f1e]" data-testid={testid}>
      <div className="px-3 py-2 flex items-center justify-between border-b border-slate-200 dark:border-slate-700/40">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-sky-500" />
          <span className="text-xs font-semibold text-slate-800 dark:text-white">{title}</span>
        </div>
        <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400">{count} linked</span>
      </div>
      {children}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="px-3 py-4 text-[10px] italic text-slate-400 dark:text-slate-500 text-center">
      {label}
    </div>
  );
}

function CardTable({ rows, columns, testidPrefix }) {
  if (!rows?.length) return <EmptyState label="No linked rows" />;
  return (
    <div className="max-h-[210px] overflow-y-auto">
      <table className="w-full text-[10.5px]">
        <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800/40">
          <tr>{columns.map(c => (
            <th key={c.key} className={`p-1.5 ${c.right ? 'text-right' : 'text-left'} text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400`}>{c.header}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-slate-100 dark:border-slate-700/30"
                data-testid={`${testidPrefix}-row-${r.code || i}`}>
              {columns.map(c => (
                <td key={c.key} className={`p-1.5 ${c.right ? 'text-right font-mono' : ''} ${c.className || 'text-slate-700 dark:text-slate-300'}`}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResourceCurve({ rows, empty, kind }) {
  if (!rows?.length) return <EmptyState label={empty} />;
  const dates = Array.from(new Set(rows.flatMap(r => (r.daily || []).map(d => d.date)))).sort();
  const dateIdx = Object.fromEntries(dates.map((d, i) => [d, i]));
  return (
    <div className="max-h-[220px] overflow-auto">
      <table className="text-[10px]">
        <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800/40">
          <tr>
            <th className="p-1 text-left sticky left-0 z-10 bg-slate-100 dark:bg-slate-800/40 min-w-[150px]">{kind}</th>
            <th className="p-1 text-right min-w-[46px]">Total</th>
            {dates.map(d => <th key={d} className="p-1 text-right font-mono min-w-[38px] whitespace-nowrap">{d.slice(5)}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const qty = Object.fromEntries((r.daily || []).map(d => [d.date, d.qty]));
            return (
              <tr key={r.resourceId} className="border-t border-slate-100 dark:border-slate-700/30">
                <td className="p-1 sticky left-0 z-10 bg-white dark:bg-[#0a0f1e] whitespace-nowrap">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${kind === 'Manpower' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                  {r.name}
                </td>
                <td className="p-1 text-right font-mono">{Number(r.total).toFixed(1)}</td>
                {dates.map(d => (
                  <td key={d} className="p-1 text-right font-mono">{qty[d] ? Number(qty[d]).toFixed(1) : '·'}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CognaraCard({ ai, cached, onReanalyze, reanalyzing, projectId, task }) {
  const risk = RISK_STYLE[(ai?.risk_level || 'MEDIUM').toUpperCase()] || RISK_STYLE.MEDIUM;
  const sections = [
    { key: 'cautions',     title: 'Cautions',        icon: AlertTriangle, color: 'text-red-500'    },
    { key: 'monitoring',   title: 'Monitor Weekly',  icon: Eye,           color: 'text-sky-500'    },
    { key: 'action_plan',  title: 'Action Plan (Do Now)', icon: ListChecks, color: 'text-emerald-500' },
    { key: 'catch_up_plan',title: 'Catch-Up Plan',   icon: TrendingDown,  color: 'text-amber-500'  },
    { key: 'cross_impact', title: 'Cross Impact',    icon: GitBranch,     color: 'text-fuchsia-500'},
  ];
  return (
    <div className="rounded-lg border border-fuchsia-200 dark:border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-50/60 to-purple-50/40 dark:from-fuchsia-500/10 dark:to-purple-500/10 p-3" data-testid="cognara-5d-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-fuchsia-500" />
          <span className="text-xs font-semibold text-slate-800 dark:text-white">Cognara Analysis</span>
          {cached && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300" data-testid="cognara-5d-cached">CACHED</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={onReanalyze} disabled={reanalyzing}
            className="text-[9.5px] px-2 py-0.5 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white flex items-center gap-1 disabled:opacity-50"
            data-testid="cognara-5d-reanalyze">
            {reanalyzing ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5" />}
            Re-analyze
          </button>
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${risk.bg} ${risk.tx}`}>{(ai?.risk_level || 'MEDIUM').toUpperCase()}</span>
        </div>
      </div>
      <div className="text-[11px] font-semibold text-slate-800 dark:text-slate-100 mb-2">{ai?.headline || '—'}</div>
      <div className="space-y-2">
        {sections.map(s => {
          const rows = ai?.[s.key] || [];
          if (!rows.length) return null;
          return (
            <div key={s.key}>
              <div className="text-[9.5px] uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-0.5 flex items-center gap-1">
                <s.icon className={`w-3 h-3 ${s.color}`} /> {s.title}
              </div>
              <ul className="text-[10.5px] text-slate-700 dark:text-slate-200 space-y-1 list-disc pl-4">
                {rows.map((r, i) => (
                  <li key={i}>
                    <span>{r}</span>
                    {projectId && task && (
                      <CognaraRaidPushInline
                        projectId={projectId}
                        section={s.key}
                        bullet={r}
                        task={task}
                        riskLevel={(ai?.risk_level || 'MEDIUM').toUpperCase()}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function LookAhead5DDrawer({ scheduleId, taskId, windowWeeks, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reanalyzing, setReanalyzing] = useState(false);

  const load = React.useCallback(async (refresh = false) => {
    if (!scheduleId || !taskId) return;
    if (refresh) setReanalyzing(true); else { setLoading(true); setData(null); }
    try {
      const r = await api.get(`/scheduling/schedules/${scheduleId}/tasks/${taskId}/5d-drilldown`, {
        params: { window_weeks: windowWeeks || 12, refresh: refresh ? true : undefined },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '5D drilldown failed');
    } finally {
      setLoading(false); setReanalyzing(false);
    }
  }, [scheduleId, taskId, windowWeeks]);

  useEffect(() => { load(false); }, [load]);

  if (!taskId) return null;
  const t = data?.task || {};
  const cached = !!data?.cognara?._fromCache;

  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-end bg-slate-900/40" onClick={onClose} data-testid="lookahead-5d-drawer">
      <div
        className="w-[720px] max-w-[95vw] bg-slate-50 dark:bg-[#050914] shadow-2xl overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-50 dark:bg-[#050914] border-b border-slate-200 dark:border-slate-700/50 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {Array.isArray(t.wbs_path) && t.wbs_path.length > 1 && (
                <div className="flex flex-wrap items-center gap-x-1 text-[10px] text-slate-500 dark:text-slate-400 leading-tight mb-0.5">
                  {t.wbs_path.slice(0, -1).map((n, i, arr) => (
                    <span key={n.code} className="flex items-center gap-1">
                      <span className="truncate max-w-[140px]" title={`${n.code} · ${n.name}`}>{n.name || n.code}</span>
                      {i < arr.length - 1 && <span className="text-slate-400 dark:text-slate-600">›</span>}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200">{t.wbs_code || '—'}</span>
                <h2 className="text-sm font-bold text-slate-800 dark:text-white truncate">{t.name || '…'}</h2>
                {t.is_critical && <span className="text-[9.5px] px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300">CRITICAL PATH</span>}
              </div>
              <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-1 flex flex-wrap gap-x-3">
                <span>Plan: {t.scheduled_start?.slice(0,10) || '—'} → {t.scheduled_finish?.slice(0,10) || '—'}</span>
                <span>Progress: <b className="text-slate-700 dark:text-slate-200">{Math.round(t.percent_complete || 0)}%</b></span>
                {t.actual_start && <span>Actual start: {t.actual_start.slice(0,10)}</span>}
              </div>
            </div>
            <button onClick={onClose} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-800" data-testid="lookahead-5d-close">
              <X className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-16 text-slate-400 dark:text-slate-500 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> <span className="text-xs">Loading 5D data + Cognara analysis…</span>
            </div>
          )}

          {!loading && data && (
            <>
              {/* Cognara narrative first — most important */}
              <CognaraCard
                ai={data.cognara}
                cached={cached}
                onReanalyze={() => load(true)}
                reanalyzing={reanalyzing}
                projectId={data.portfolio_project_id}
                task={{ ...t, id: taskId }}
              />

              {/* Engineering */}
              <Section icon={Factory} title="Engineering (DCI)" count={data.engineering.length} testid="section-engineering">
                <CardTable
                  rows={data.engineering}
                  testidPrefix="eng"
                  columns={[
                    { key: 'code', header: 'Doc No' },
                    { key: 'name', header: 'Title', className: 'text-slate-700 dark:text-slate-300 truncate max-w-[220px]' },
                    { key: 'discipline', header: 'Disc' },
                    { key: 'status', header: 'Status', render: r => <span className={STATUS_COLOR(r.status)}>{r.status || '—'}</span> },
                    { key: 'progress', header: '% Done', right: true, render: r => `${Math.round(r.progress || 0)}%` },
                    { key: 'planFinish', header: 'Plan Fin', right: true, render: r => (r.planFinish || '').slice(0,10) || '—' },
                  ]}
                />
              </Section>

              {/* Procurement */}
              <Section icon={Package} title="Procurement (MCI)" count={data.procurement.length} testid="section-procurement">
                <CardTable
                  rows={data.procurement}
                  testidPrefix="proc"
                  columns={[
                    { key: 'code', header: 'MCI#' },
                    { key: 'name', header: 'Material', className: 'text-slate-700 dark:text-slate-300 truncate max-w-[200px]' },
                    { key: 'phase', header: 'Phase' },
                    { key: 'poNumber', header: 'PO' },
                    { key: 'status', header: 'Status', render: r => <span className={STATUS_COLOR(r.status)}>{r.status || '—'}</span> },
                    { key: 'progress', header: '% Done', right: true, render: r => `${Math.round(r.progress || 0)}%` },
                  ]}
                />
              </Section>

              {/* Fabrication & Construction */}
              <Section icon={Hammer} title="Fabrication & Construction (F&C)" count={data.construction.length} testid="section-construction">
                <CardTable
                  rows={data.construction}
                  testidPrefix="fc"
                  columns={[
                    { key: 'code', header: 'Code' },
                    { key: 'name', header: 'Activity', className: 'text-slate-700 dark:text-slate-300 truncate max-w-[220px]' },
                    { key: 'discipline', header: 'Disc' },
                    { key: 'status', header: 'Status', render: r => <span className={STATUS_COLOR(r.status)}>{r.status || '—'}</span> },
                    { key: 'progress', header: '% Done', right: true, render: r => `${Math.round(r.progress || 0)}%` },
                    { key: 'planFinish', header: 'Plan Fin', right: true, render: r => (r.planFinish || '').slice(0,10) || '—' },
                  ]}
                />
              </Section>

              {/* Manpower */}
              <Section icon={Users} title="Manpower Requirement (daily)" count={data.manpower.length} testid="section-manpower">
                <ResourceCurve rows={data.manpower} kind="Manpower" empty="No manpower assigned to this activity" />
              </Section>

              {/* Equipment */}
              <Section icon={Wrench} title="Equipment Requirement (daily)" count={data.equipment.length} testid="section-equipment">
                <ResourceCurve rows={data.equipment} kind="Equipment" empty="No equipment assigned to this activity" />
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
